import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { fetchMySessions, deleteSession, renameSession } from '../../api/sessions';
import { useCertStore } from '../../stores/certStore';
import type { SessionSummary } from '../../types';
import { Button, Card, TextField } from '../ui';

function timeAgo(dateStr: string, t: TFunction): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('dashboard.study.sessions.justNow');
  if (mins < 60) return t('dashboard.study.sessions.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('dashboard.study.sessions.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('dashboard.study.sessions.daysAgo', { count: days });
}

function ModeBadge({ mode }: { mode: string }) {
  const { t } = useTranslation();
  const isExam = mode === 'exam';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        isExam
          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      }`}
    >
      {isExam ? t('dashboard.study.sessions.modeExam') : t('dashboard.study.sessions.modeStudy')}
    </span>
  );
}

function SessionCard({
  session,
  onResume,
  onDelete,
  onRename,
}: {
  session: SessionSummary;
  onResume: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(session.session_name);
  const [showConfirm, setShowConfirm] = useState(false);

  const progress =
    session.total_questions > 0
      ? Math.round((session.cursor_global / session.total_questions) * 100)
      : 0;
  const accuracy =
    session.total_answered > 0
      ? Math.round((session.correct_count / session.total_answered) * 100)
      : 0;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        {editing ? (
          <form
            className="flex flex-1 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onRename(name);
              setEditing(false);
            }}
          >
            <TextField
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              wrapperClassName="flex-1"
            />
            <button type="submit" className="text-xs font-medium text-blue-600 dark:text-blue-400">
              {t('dashboard.study.sessions.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setName(session.session_name);
                setEditing(false);
              }}
              className="text-xs text-slate-400 dark:text-slate-500"
            >
              {t('dashboard.study.sessions.cancel')}
            </button>
          </form>
        ) : (
          <div className="min-w-0 flex-1">
            <button onClick={() => setEditing(true)} className="text-left">
              <h3 className="truncate font-semibold text-slate-900 dark:text-white">
                {session.session_name}
              </h3>
            </button>
            <div className="mt-1 flex flex-wrap gap-2 text-xs">
              <ModeBadge mode={session.mode} />
              {session.topic_filter &&
                session.topic_filter.split(',').map((topic) => (
                  <span
                    key={topic}
                    className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                  >
                    {topic.trim()}
                  </span>
                ))}
              {session.difficulty_filter &&
                session.difficulty_filter.split(',').map((d) => (
                  <span
                    key={d}
                    className="rounded-full bg-slate-100 px-2 py-0.5 capitalize text-slate-600 dark:bg-white/5 dark:text-slate-300"
                  >
                    {d.trim()}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>
            {t('dashboard.study.sessions.progressOf', { cursor: session.cursor_global, total: session.total_questions })}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
          {session.total_answered > 0 && (
            <span>
              {t('dashboard.study.sessions.correctAccuracy', { correct: session.correct_count, answered: session.total_answered, accuracy })}
            </span>
          )}
          <span className="ml-auto">{timeAgo(session.updated_at, t)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto flex gap-2">
        <Button variant="primary" size="sm" onClick={onResume} className="flex-1">
          {t('dashboard.study.sessions.resume')}
        </Button>
        {showConfirm ? (
          <div className="flex gap-1">
            <button
              onClick={onDelete}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
            >
              {t('dashboard.study.sessions.yes')}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5"
            >
              {t('dashboard.study.sessions.no')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowConfirm(true)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-400 transition-colors hover:border-red-300 hover:text-red-500 dark:border-white/10 dark:text-slate-500 dark:hover:border-red-500/40 dark:hover:text-red-400"
            title={t('dashboard.study.sessions.deleteTitle')}
            aria-label={t('dashboard.study.sessions.deleteTitle')}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>
        )}
      </div>
    </Card>
  );
}

export default function ActiveSessionsGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const certCode = useCertStore((s) => s.certCode);

  const { data, isLoading } = useQuery({
    queryKey: ['my-sessions', certCode, 'active-tab'],
    queryFn: () => fetchMySessions({ status: 'active', limit: 50 }, certCode),
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: deleteSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-sessions'] }),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => renameSession(id, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-sessions'] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card className="flex flex-col items-center py-16 text-center">
        <p className="text-lg text-slate-500 dark:text-slate-400">
          {t('dashboard.study.sessions.emptyTitle')}
        </p>
        <p className="mt-1 text-sm text-slate-400 dark:text-slate-500">
          {t('dashboard.study.sessions.emptyDescription')}
        </p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {data.items.map((s) => (
        <SessionCard
          key={s.id}
          session={s}
          onResume={() => navigate(`/study/session/${s.id}`)}
          onDelete={() => deleteMut.mutate(s.id)}
          onRename={(name) => renameMut.mutate({ id: s.id, name })}
        />
      ))}
    </div>
  );
}
